"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCodeImport from "react-qr-code";
const QRCode = (QRCodeImport as any)?.default ?? QRCodeImport;
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {
    Check,
    Copy,
    Download,
    ExternalLink,
    Link as LinkIcon,
    Mail,
    MessageCircle,
    MessageSquare,
    RotateCcw,
    ScanLine,
} from "lucide-react";
import { TurnstileForm } from "../../forms/TurnstileForm";
import { productInfoCardTokens as tokens } from "./ProductInfoCard.tokens";
import { createProductInfoCardStyles } from "./ProductInfoCard.styles";
import type {
    ProductData,
    ProductDimensions,
    ProductImage,
    ProductInfoCardProps,
    ProductPricing,
} from "./ProductInfoCard.types";
import {
    buildEnquiryText,
    buildShareText,
    buildSmsEnquiryHref,
    buildWhatsAppEnquiryHref,
    cloneProduct,
    formatPrice,
    normaliseProduct,
} from "./ProductInfoCard.helpers";
import {
    buildProductEnquiryHiddenPayload,
    buildProductEnquiryReadOnlyFields,
    getProductStockStatus,
    productEnquirySchema,
} from "./ProductInfoCard.enquiry";
export type {
    ProductData,
    ProductDimensions,
    ProductImage,
    ProductInfoCardProps,
    ProductPricing,
} from "./ProductInfoCard.types";

export default function ProductInfoCard({
                                            product,
                                            className = "",
                                            currency = "AUD",
                                            showGallery = true,
                                            showOpenProductPage = false,
                                            showEnquireNow = true,
                                            ctaText = "Enquire Now",
                                            enquiryButtonText,
                                            ctaAction,
                                            ctaColor = tokens.colors.primaryCta,
                                            eyebrowText = "Product Details",
                                            eyebrowBackgroundColor = "rgba(17, 17, 17, 0.03)",
                                            eyebrowTextColor = tokens.colors.textMuted,
                                            showNoPriceCta = false,
                                            noPriceCtaText = "Contact Us",
                                            noPriceCtaAction,
                                            debugMode = false,
                                            enableEnquiryModal = false,
                                            enquiryWorkerUrl,
                                            enquirySiteKey,
                                            enquiryFormName = "product-enquiry",
                                            enquiryModalTitle,
                                            enquirySubmitText = "Send Enquiry",
                                            enquirySuccessMessage = "✅ Product enquiry received. We’ll get back to you shortly.",
                                            enquiryPhoneNumber,
                                            showEnquiryWhatsApp = true,
                                            showEnquirySms = true,
                                            enquiryWhatsAppText = "WhatsApp",
                                            enquirySmsText = "SMS",
                                            enquiryMessage,
                                        }: ProductInfoCardProps) {
    const [copied, setCopied] = useState(false);
    const [jsonCopied, setJsonCopied] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
    const [hoveredTile, setHoveredTile] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<"preview" | "edit">("preview");
    const [heroLoaded, setHeroLoaded] = useState(false);
    const heroImageRef = useRef<HTMLImageElement | null>(null);
    const [editableProduct, setEditableProduct] = useState<ProductData>(() =>
        normaliseProduct(cloneProduct(product)),
    );

    useEffect(() => {
        setEditableProduct(normaliseProduct(cloneProduct(product)));
    }, [product]);

    const activeProduct = debugMode ? editableProduct : product;

    const name = activeProduct?.basic?.name || activeProduct?.title || "Product";
    const subtitle = activeProduct?.basic?.subtitle || "";
    const stockStatus = getProductStockStatus(activeProduct);
    const description = activeProduct?.description || "";
    const features = activeProduct?.features || [];
    const pricing = activeProduct?.pricing || [];
    const dimensions = activeProduct?.attributes?.dimensions || {};
    const canonicalUrl = activeProduct?.seo?.canonicalUrl || "";
    const featureImage = activeProduct?.featureImage;
    const featureImageSrc = featureImage?.original || featureImage?.thumbnail || "";
    const galleryImages = activeProduct?.images || [];

    useEffect(() => {
        setHeroLoaded(false);

        const image = heroImageRef.current;

        if (image?.complete && image.naturalWidth > 0) {
            setHeroLoaded(true);
        }
    }, [featureImageSrc]);

    const mainPrice = pricing[0];
    const subPricing = pricing.slice(1);

    const shareText = useMemo(
        () => buildShareText(name, subtitle),
        [name, subtitle],
    );

    const whatsappHref = canonicalUrl
        ? `https://wa.me/?text=${encodeURIComponent(`${shareText} ${canonicalUrl}`)}`
        : "";
    const smsHref = canonicalUrl
        ? `sms:?&body=${encodeURIComponent(`${shareText} ${canonicalUrl}`)}`
        : "";

    const enquiryText = useMemo(
        () => buildEnquiryText(name, canonicalUrl, enquiryMessage),
        [name, canonicalUrl, enquiryMessage],
    );
    const whatsappEnquiryHref = buildWhatsAppEnquiryHref(
        enquiryPhoneNumber,
        enquiryText,
    );
    const smsEnquiryHref = buildSmsEnquiryHref(enquiryPhoneNumber, enquiryText);
    const hasEnquiryContactButtons = Boolean(
        enquiryPhoneNumber &&
        ((showEnquiryWhatsApp && whatsappEnquiryHref) ||
            (showEnquirySms && smsEnquiryHref)),
    );

    const hasDimensions = Boolean(
        dimensions.width || dimensions.height || dimensions.depth || dimensions.length,
    );

    const slides = galleryImages
        .filter((image) => image.original)
        .map((image) => ({
            src: image.original as string,
            alt: image.alt || name,
        }));

    const enquiryModalReady = Boolean(
        enableEnquiryModal && enquiryWorkerUrl && enquirySiteKey,
    );
    const resolvedEnquiryButtonText = enquiryButtonText || ctaText;
    const enquiryDisplayImage = featureImageSrc
        ? {
            src: featureImageSrc,
            alt: featureImage?.alt || name,
        }
        : undefined;

    const enquiryReadOnlyFields = useMemo(
        () => buildProductEnquiryReadOnlyFields(activeProduct),
        [activeProduct, canonicalUrl],
    );
    const enquiryHiddenPayload = useMemo(
        () => buildProductEnquiryHiddenPayload(activeProduct, canonicalUrl),
        [activeProduct, canonicalUrl],
    );

    const jsonOutput = useMemo(
        () => JSON.stringify(normaliseProduct(editableProduct), null, 2),
        [editableProduct],
    );

    const updateProduct = (updater: (current: ProductData) => ProductData) => {
        setEditableProduct((current) =>
            normaliseProduct(updater(cloneProduct(current))),
        );
    };

    const setBasicField = (field: "name" | "subtitle", value: string) => {
        updateProduct((current) => ({
            ...current,
            basic: {
                ...current.basic,
                [field]: value,
            },
        }));
    };

    const setDescription = (value: string) => {
        updateProduct((current) => ({
            ...current,
            description: value,
        }));
    };

    const setCanonicalUrl = (value: string) => {
        updateProduct((current) => ({
            ...current,
            seo: {
                ...current.seo,
                canonicalUrl: value,
            },
        }));
    };

    const setDimensionField = (field: keyof ProductDimensions, value: string) => {
        updateProduct((current) => ({
            ...current,
            attributes: {
                ...current.attributes,
                dimensions: {
                    ...current.attributes?.dimensions,
                    [field]: value,
                },
            },
        }));
    };

    const setFeatureImageField = (
        field: "original" | "thumbnail" | "alt",
        value: string,
    ) => {
        updateProduct((current) => ({
            ...current,
            featureImage: {
                ...current.featureImage,
                [field]: value,
            },
        }));
    };

    const updatePricingItem = (
        index: number,
        field: keyof ProductPricing,
        value: string,
    ) => {
        updateProduct((current) => {
            const nextPricing = [...(current.pricing || [])];
            const existing = nextPricing[index] || {};

            nextPricing[index] = {
                ...existing,
                [field]:
                    field === "price"
                        ? value.trim() === ""
                            ? null
                            : Number(value)
                        : value,
            };

            return {
                ...current,
                pricing: nextPricing,
            };
        });
    };

    const addPricingItem = () => {
        updateProduct((current) => ({
            ...current,
            pricing: [...(current.pricing || []), { label: "", price: null }],
        }));
    };

    const removePricingItem = (index: number) => {
        updateProduct((current) => ({
            ...current,
            pricing: (current.pricing || []).filter((_, i) => i !== index),
        }));
    };

    const updateFeature = (index: number, value: string) => {
        updateProduct((current) => {
            const nextFeatures = [...(current.features || [])];
            nextFeatures[index] = value;

            return {
                ...current,
                features: nextFeatures,
            };
        });
    };

    const addFeature = () => {
        updateProduct((current) => ({
            ...current,
            features: [...(current.features || []), ""],
        }));
    };

    const removeFeature = (index: number) => {
        updateProduct((current) => ({
            ...current,
            features: (current.features || []).filter((_, i) => i !== index),
        }));
    };

    const updateGalleryImage = (
        index: number,
        field: keyof ProductImage,
        value: string,
    ) => {
        updateProduct((current) => {
            const nextImages = [...(current.images || [])];
            const existing = nextImages[index] || {};

            nextImages[index] = {
                ...existing,
                [field]: value,
            };

            return {
                ...current,
                images: nextImages,
            };
        });
    };

    const addGalleryImage = () => {
        updateProduct((current) => ({
            ...current,
            images: [
                ...(current.images || []),
                {
                    id: "",
                    original: "",
                    thumbnail: "",
                    alt: "",
                },
            ],
        }));
    };

    const removeGalleryImage = (index: number) => {
        updateProduct((current) => ({
            ...current,
            images: (current.images || []).filter((_, i) => i !== index),
        }));
    };

    const handleCopyLink = async () => {
        if (!canonicalUrl) return;

        try {
            await navigator.clipboard.writeText(canonicalUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error("Failed to copy link", error);
        }
    };

    const handleCopyJson = async () => {
        try {
            await navigator.clipboard.writeText(jsonOutput);
            setJsonCopied(true);
            window.setTimeout(() => setJsonCopied(false), 1800);
        } catch (error) {
            console.error("Failed to copy JSON", error);
        }
    };

    const handleDownloadJson = () => {
        try {
            const blob = new Blob([jsonOutput], {
                type: "application/json;charset=utf-8",
            });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");

            const safeId = (editableProduct?.id || "")
                .toLowerCase()
                .replace(/[^a-z0-9-_]+/g, "-")
                .replace(/^-+|-+$/g, "");

            const fallbackName =
                (editableProduct?.basic?.name || "product")
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "") || "product";

            anchor.href = url;
            anchor.download = `${safeId || fallbackName}.json`;

            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download JSON", error);
        }
    };

    const handleResetEdits = () => {
        setEditableProduct(normaliseProduct(cloneProduct(product)));
    };

    const handleEnquireNow = () => {
        if (ctaAction) {
            ctaAction();
            return;
        }

        if (canonicalUrl && typeof window !== "undefined") {
            window.location.href = canonicalUrl;
        }
    };

    const handleNoPriceCta = () => {
        if (noPriceCtaAction) {
            noPriceCtaAction();
            return;
        }

        handleEnquireNow();
    };

    const styles = createProductInfoCardStyles({ ctaColor });

    return (
        <section style={styles.root} className={className}>
            <style>{`
                @keyframes productInfoCardSkeleton {
                    0% {
                        background-position: 100% 50%;
                    }
                    100% {
                        background-position: 0 50%;
                    }
                }

                @media (max-width: 900px) {
                    .product-info-card-debug-grid {
                        grid-template-columns: 1fr !important;
                    }
                }

                @media (max-width: 600px) {
                    .product-info-card-top-grid {
                        grid-template-columns: 1fr !important;
                    }

                    .product-info-card-share-section {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            <div style={styles.layout}>
                {debugMode ? (
                    <div style={styles.tabBar}>
                        <button
                            type="button"
                            onClick={() => setActiveTab("preview")}
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === "preview" ? styles.tabButtonActive : {}),
                            }}
                        >
                            Preview
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("edit")}
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === "edit" ? styles.tabButtonActive : {}),
                            }}
                        >
                            Edit Product
                        </button>
                    </div>
                ) : null}

                {debugMode && activeTab === "edit" ? (
                    <div style={styles.debugPanel}>
                        <div style={styles.debugActions}>
                            <button
                                type="button"
                                onClick={handleDownloadJson}
                                style={{ ...styles.buttonBase, ...styles.buttonCta }}
                            >
                                <Download size={15} />
                                <span>Save JSON</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleCopyJson}
                                style={{ ...styles.buttonBase, ...styles.buttonPrimary }}
                            >
                                {jsonCopied ? <Check size={15} /> : <Copy size={15} />}
                                <span>{jsonCopied ? "JSON Copied" : "Copy JSON"}</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleResetEdits}
                                style={{ ...styles.buttonBase, ...styles.buttonSecondary }}
                            >
                                <RotateCcw size={15} />
                                <span>Reset</span>
                            </button>
                        </div>

                        <p style={styles.helperText}>
                            Edit the product locally, then save the full JSON file using the
                            product id as the filename.
                        </p>

                        <div
                            className="product-info-card-debug-grid"
                            style={styles.debugGrid}
                        >
                            {/* existing debug form unchanged */}
                        </div>
                    </div>
                ) : null}

                {!debugMode || activeTab === "preview" ? (
                    <div style={styles.main}>
                        <div className="product-info-card-top-grid" style={styles.topGrid}>
                            <div style={styles.infoColumn}>
                                <div style={styles.header}>
                                    <div
                                        style={{
                                            ...styles.eyebrow,
                                            background: eyebrowBackgroundColor,
                                            color: eyebrowTextColor,
                                        }}
                                    >
                                        {eyebrowText}
                                    </div>
                                    <h1 style={styles.title}>{name}</h1>
                                    {subtitle ? <p style={styles.subtitle}>{subtitle}</p> : null}
                                </div>

                                {pricing.length > 0 ? (
                                    <div style={styles.priceBlock}>
                                        {mainPrice ? (
                                            <div style={styles.pricingList}>
                                                <div style={styles.pricingRow}>
                          <span style={styles.pricingLabel}>
                            {mainPrice.label || "Price"}
                          </span>

                                                    {mainPrice.price === null ||
                                                    mainPrice.price === undefined ||
                                                    mainPrice.price <= 0 ? (
                                                        <div style={styles.noPriceWrap}>
                              <span style={styles.pricingValueNoPrice}>
                                Contact for price
                              </span>

                                                            {showNoPriceCta ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={handleNoPriceCta}
                                                                    style={{
                                                                        ...styles.buttonBase,
                                                                        ...styles.buttonNoPriceCta,
                                                                        minHeight: 36,
                                                                        padding: "8px 12px",
                                                                        fontSize: 12,
                                                                    }}
                                                                >
                                                                    <span>{noPriceCtaText}</span>
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    ) : (
                                                        <span style={styles.pricingValue}>
                              {formatPrice(mainPrice.price, currency)}
                            </span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : null}

                                        {subPricing.length > 0 ? (
                                            <div style={styles.subPricingList}>
                                                {subPricing.map((item, index) => (
                                                    <div
                                                        key={`${item.label || "sub-price"}-${index}`}
                                                        style={styles.subPricingRow}
                                                    >
                            <span style={styles.subPricingLabel}>
                              {item.label || "Option"}
                            </span>
                                                        <span style={styles.subPricingValue}>
                              {formatPrice(item.price, currency)}
                            </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}

                                {showOpenProductPage && canonicalUrl ? (
                                    <div style={styles.ctaRow}>
                                        <a
                                            href={canonicalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                ...styles.buttonBase,
                                                ...styles.buttonTertiary,
                                            }}
                                        >
                                            <ExternalLink size={15} />
                                            <span>Open Product Page</span>
                                        </a>
                                    </div>
                                ) : null}

                                {description ? (
                                    <div style={styles.contentBlock}>
                                        <p style={styles.body}>{description}</p>
                                    </div>
                                ) : null}

                                {features.length > 0 ? (
                                    <div style={styles.contentBlock}>
                                        <div style={styles.featureList}>
                                            {features.map((feature, index) => (
                                                <span
                                                    key={`${feature}-${index}`}
                                                    style={styles.featureChip}
                                                >
                          {feature}
                        </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {hasDimensions ? (
                                    <div style={styles.contentBlock}>
                                        <h2 style={styles.sectionTitle}>Dimensions</h2>
                                        <div style={styles.dimensionGrid}>
                                            <div style={styles.dimensionItem}>
                                                <div style={styles.dimensionLabel}>Width</div>
                                                <div style={styles.dimensionValue}>
                                                    {dimensions.width || "-"}
                                                </div>
                                            </div>

                                            <div style={styles.dimensionItem}>
                                                <div style={styles.dimensionLabel}>Height</div>
                                                <div style={styles.dimensionValue}>
                                                    {dimensions.height || "-"}
                                                </div>
                                            </div>

                                            <div style={styles.dimensionItem}>
                                                <div style={styles.dimensionLabel}>Depth</div>
                                                <div style={styles.dimensionValue}>
                                                    {dimensions.depth || "-"}
                                                </div>
                                            </div>

                                            <div style={styles.dimensionItem}>
                                                <div style={styles.dimensionLabel}>Length</div>
                                                <div style={styles.dimensionValue}>
                                                    {dimensions.length || "-"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div style={styles.mediaColumn}>
                                {featureImageSrc ? (
                                    <button
                                        type="button"
                                        aria-label={`Open featured image for ${name}`}
                                        onClick={() => setLightboxIndex(0)}
                                        style={{
                                            ...styles.heroWrap,
                                            position: "relative",
                                            overflow: "hidden",
                                            transition:
                                                "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                                            padding: 0,
                                            cursor: slides.length > 0 ? "pointer" : "default",
                                            appearance: "none",
                                            background: "#f3f4f6",
                                        }}
                                        disabled={slides.length === 0}
                                    >
                                        {!heroLoaded ? (
                                            <div
                                                aria-hidden="true"
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    zIndex: 0,
                                                    background:
                                                        "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)",
                                                    backgroundSize: "400% 100%",
                                                    animation:
                                                        "productInfoCardSkeleton 1.35s ease infinite",
                                                }}
                                            />
                                        ) : null}

                                        <img
                                            ref={heroImageRef}
                                            src={featureImageSrc}
                                            alt={featureImage?.alt || name}
                                            onLoad={() => setHeroLoaded(true)}
                                            onError={() => setHeroLoaded(true)}
                                            style={{
                                                ...styles.heroImage,
                                                position: "relative",
                                                zIndex: 1,
                                                opacity: heroLoaded ? 1 : 0,
                                                transition: "opacity 0.25s ease",
                                            }}
                                        />
                                    </button>
                                ) : null}

                                {showGallery && galleryImages.length > 0 ? (
                                    <div style={styles.galleryGrid}>
                                        {galleryImages.map((image, index) => {
                                            const tileSrc = image.thumbnail || image.original || "";
                                            const isHovered = hoveredTile === index;

                                            return (
                                                <button
                                                    key={image.id || `${tileSrc}-${index}`}
                                                    type="button"
                                                    aria-label={`Open gallery image ${index + 1}`}
                                                    onClick={() => setLightboxIndex(index)}
                                                    onMouseEnter={() => setHoveredTile(index)}
                                                    onMouseLeave={() => setHoveredTile(null)}
                                                    style={{
                                                        ...styles.galleryTileBase,
                                                        transform: isHovered
                                                            ? "translateY(-2px)"
                                                            : "translateY(0)",
                                                        boxShadow: isHovered
                                                            ? tokens.colors.hoverShadow
                                                            : "none",
                                                        borderColor: isHovered
                                                            ? "rgba(17, 17, 17, 0.16)"
                                                            : tokens.colors.border,
                                                    }}
                                                >
                                                    <img
                                                        src={tileSrc}
                                                        alt={image.alt || `${name} ${index + 1}`}
                                                        style={styles.galleryImage}
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {showEnquireNow || hasEnquiryContactButtons ? (
                            <div style={styles.enquirySection}>
                                <div style={styles.enquiryHeader}>
                                    <h2 style={styles.shareTitle}>
                                        <Mail size={15} strokeWidth={2.2} />
                                        <span>Enquire</span>
                                    </h2>
                                    <p style={styles.shareText}>
                                        Ask about availability, finishes, delivery, pricing, or dimensions.
                                    </p>
                                </div>

                                <div style={styles.shareActions}>
                                    {showEnquireNow ? (
                                        enquiryModalReady ? (
                                            <TurnstileForm
                                                renderMode="modal-button"
                                                triggerText={resolvedEnquiryButtonText}
                                                triggerVariant="blue"
                                                triggerIcon={Mail}
                                                triggerIconPosition="before"
                                                modalTitle={enquiryModalTitle || `Enquire about ${name}`}
                                                modalCloseText="Close"
                                                schema={productEnquirySchema}
                                                workerUrl={enquiryWorkerUrl as string}
                                                siteKey={enquirySiteKey as string}
                                                formName={enquiryFormName}
                                                layout="single-column"
                                                maxWidth="md"
                                                submitText={enquirySubmitText}
                                                successMessage={enquirySuccessMessage}
                                                displayImage={enquiryDisplayImage}
                                                readOnlyFields={enquiryReadOnlyFields}
                                                hiddenPayload={{
                                                    ...enquiryHiddenPayload,
                                                    stockStatus,
                                                }}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleEnquireNow}
                                                style={{
                                                    ...styles.buttonBase,
                                                    ...styles.buttonCta,
                                                }}
                                            >
                                                <Mail size={15} />
                                                <span>{resolvedEnquiryButtonText}</span>
                                            </button>
                                        )
                                    ) : null}

                                    {showEnquiryWhatsApp && whatsappEnquiryHref ? (
                                        <a
                                            href={whatsappEnquiryHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                ...styles.buttonBase,
                                                ...styles.buttonSecondary,
                                            }}
                                        >
                                            <MessageCircle size={15} />
                                            <span>{enquiryWhatsAppText}</span>
                                        </a>
                                    ) : null}

                                    {showEnquirySms && smsEnquiryHref ? (
                                        <a
                                            href={smsEnquiryHref}
                                            style={{
                                                ...styles.buttonBase,
                                                ...styles.buttonSecondary,
                                            }}
                                        >
                                            <MessageSquare size={15} />
                                            <span>{enquirySmsText}</span>
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <div
                            className="product-info-card-share-section"
                            style={styles.shareSection}
                        >
                            {canonicalUrl ? (
                                <div style={styles.shareQrWrap}>
                                    <QRCode
                                        value={canonicalUrl}
                                        size={104}
                                        style={{ width: "100%", height: "auto" }}
                                        viewBox="0 0 256 256"
                                    />
                                </div>
                            ) : (
                                <div style={styles.emptyState}>No URL</div>
                            )}

                            <div style={styles.shareContent}>
                                <div style={styles.shareHeader}>
                                    <h2 style={styles.shareTitle}>
                                        <ScanLine size={15} strokeWidth={2.2} />
                                        <span>Share</span>
                                    </h2>
                                    <p style={styles.shareText}>
                                        Scan, copy, or send this product link.
                                    </p>
                                </div>

                                {canonicalUrl ? (
                                    <div style={styles.qrLink}>{canonicalUrl}</div>
                                ) : null}

                                <div style={styles.shareActions}>
                                    <button
                                        type="button"
                                        onClick={handleCopyLink}
                                        disabled={!canonicalUrl}
                                        style={{
                                            ...styles.buttonBase,
                                            ...styles.buttonPrimary,
                                            opacity: canonicalUrl ? 1 : 0.5,
                                            cursor: canonicalUrl ? "pointer" : "not-allowed",
                                        }}
                                    >
                                        {copied ? <Check size={15} /> : <Copy size={15} />}
                                        <span>{copied ? "Link Copied" : "Copy Link"}</span>
                                    </button>

                                    <a
                                        href={whatsappHref || undefined}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-disabled={!canonicalUrl}
                                        style={{
                                            ...styles.buttonBase,
                                            ...(canonicalUrl
                                                ? styles.buttonSecondary
                                                : styles.buttonSecondaryDisabled),
                                        }}
                                    >
                                        <MessageCircle size={15} />
                                        <span>WhatsApp</span>
                                    </a>

                                    <a
                                        href={smsHref || undefined}
                                        style={{
                                            ...styles.buttonBase,
                                            ...(canonicalUrl
                                                ? styles.buttonSecondary
                                                : styles.buttonSecondaryDisabled),
                                        }}
                                    >
                                        <MessageSquare size={15} />
                                        <span>SMS</span>
                                    </a>
                                </div>

                                {canonicalUrl ? (
                                    <div style={styles.rawLinkRow}>
                                        <LinkIcon size={13} />
                                        <span>Ready to share</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            {slides.length > 0 ? (
                <Lightbox
                    open={lightboxIndex >= 0}
                    close={() => setLightboxIndex(-1)}
                    index={lightboxIndex >= 0 ? lightboxIndex : 0}
                    slides={slides}
                />
            ) : null}
        </section>
    );
}